// apps/web-pwa/src/pages/RespondPage.tsx
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@catchup/convex/convex/_generated/api';
import { format } from 'date-fns';
import { HeatmapVisualization } from '../components/HeatmapVisualization';
import { QuorumIndicator } from '../components/QuorumIndicator';
import { InviteSubmittedScreen } from '../components/InviteSubmittedScreen';
import {
  AvailabilityPickerScreen,
  type PickerDayGroup,
} from '../components/AvailabilityPickerScreen';
import type { Id } from '@catchup/convex/convex/_generated/dataModel';

export default function RespondPage() {
  const { token } = useParams<{ token: string }>();
  const [tokenHash, setTokenHash] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Hash the token for secure lookup
  useEffect(() => {
    if (token) {
      // In browser environment, we need to use Web Crypto API
      const hashToken = async () => {
        const encoder = new TextEncoder();
        const data = encoder.encode(token);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        setTokenHash(hashHex);
      };
      hashToken();
    }
  }, [token]);

  // Get magic link token data
  const magicLinkData = useQuery(
    api.magicLinkTokens.getByTokenHash,
    tokenHash ? { tokenHash } : 'skip'
  );

  // Get event data if we have a valid token
  const eventData = useQuery(
    api.events.getEventWithParticipants,
    magicLinkData?.eventId ? { eventId: magicLinkData.eventId } : 'skip'
  );

  // Get heatmap data
  const heatmapData = useQuery(
    api.heatmap.getHeatmap,
    magicLinkData?.eventId ? { eventId: magicLinkData.eventId } : 'skip'
  );

  // TODO(picker): persist `payload.responses` and `payload.note` to the
  // participant's response record (Convex mutation TBD), and persist
  // `payload.smsConsent` to the participants table per
  // packages/convex/convex/schema.ts:403-409 with
  // `source: "pwa_after_response"`. For now we just flip to the submitted
  // confirmation screen so the existing flow keeps working.
  const handleSubmit = () => {
    setSubmitted(true);
  };

  // TODO(picker): the existing event model carries `selectedSlots: string[]`
  // and free-form `timeframe`; it does NOT yet expose structured
  // (dayId, startMs, endMs, mealtag) slot windows that the new picker needs.
  // Until the slot-generation pipeline lands, we render a hard-coded demo set
  // that mirrors the signed-off prototype. Replace `demoDays` with the real
  // availability windows derived from `eventData` when available.
  const demoDays: PickerDayGroup[] = [
    {
      dayId: '2026-05-21',
      dayLabel: 'Tuesday, May 21',
      slots: [
        {
          id: '2026-05-21T18:00',
          startMs: Date.UTC(2026, 4, 21, 22, 0), // 6:00 PM ET-ish; rendered in viewer locale
          endMs: Date.UTC(2026, 4, 21, 23, 30),
          mealtag: 'Dinner',
        },
        {
          id: '2026-05-21T19:30',
          startMs: Date.UTC(2026, 4, 21, 23, 30),
          endMs: Date.UTC(2026, 4, 22, 1, 0),
          mealtag: 'After work',
        },
      ],
    },
    {
      dayId: '2026-05-22',
      dayLabel: 'Wednesday, May 22',
      slots: [
        {
          id: '2026-05-22T12:00',
          startMs: Date.UTC(2026, 4, 22, 16, 0),
          endMs: Date.UTC(2026, 4, 22, 17, 30),
          mealtag: 'Lunch',
        },
        {
          id: '2026-05-22T18:30',
          startMs: Date.UTC(2026, 4, 22, 22, 30),
          endMs: Date.UTC(2026, 4, 23, 0, 0),
          mealtag: 'After work',
        },
      ],
    },
    {
      dayId: '2026-05-25',
      dayLabel: 'Saturday, May 25',
      slots: [
        {
          id: '2026-05-25T11:00',
          startMs: Date.UTC(2026, 4, 25, 15, 0),
          endMs: Date.UTC(2026, 4, 25, 16, 30),
          mealtag: 'Brunch',
        },
        {
          id: '2026-05-25T15:00',
          startMs: Date.UTC(2026, 4, 25, 19, 0),
          endMs: Date.UTC(2026, 4, 25, 20, 30),
          mealtag: 'Afternoon coffee',
        },
      ],
    },
  ];

  // Loading state
  if (!token || !tokenHash) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse mb-2">
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for invalid or expired token
  if (magicLinkData === null || (magicLinkData && magicLinkData.isRevoked)) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div
            data-testid="error-message"
            className="bg-red-50 border border-red-200 rounded-lg p-6 text-center"
          >
            <p className="text-red-800 text-lg font-semibold">
              Invalid or expired link
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Still loading event data
  if (!eventData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse mb-2">
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
          </div>
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  // Calculate quorum metrics
  const quorumRequired = eventData.quorumRules?.minRequired || 2;
  const totalParticipants = eventData.participants?.length || 0;
  const respondedCount = eventData.participants?.filter((p: { hasResponded: boolean }) => p.hasResponded).length || 0;
  const hasAnySlotMeetingQuorum = heatmapData?.slots?.some((slot: { quorumMet: boolean }) => slot.quorumMet) ?? false;

  // Story 5.6: Check if event is confirmed
  const isConfirmed = eventData.status === 'confirmed';
  const confirmedSlotStart = eventData.confirmedSlotStart;

  // Format confirmed date with day name and AM/PM (e.g., "Friday, December 20 at 10:00 AM")
  const formatConfirmedDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return format(date, "EEEE, MMMM d 'at' h:mm a");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Event Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {eventData.title}
          </h1>
          {eventData.timeframe && (
            <p className="text-gray-600 mb-2">
              Timeframe: {eventData.timeframe}
            </p>
          )}
          {eventData.duration && (
            <p className="text-gray-600 mb-4">
              Duration: {eventData.duration}
            </p>
          )}
          {eventData.location && (
            <p className="text-gray-600 mb-4">
              Location: {eventData.location.name}
            </p>
          )}
        </div>

        {/* Story 5.6: Confirmed State Display */}
        {isConfirmed && (
          <div
            data-testid="confirmed-banner"
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-6 text-center"
          >
            <div className="flex items-center justify-center mb-3">
              <span className="text-3xl mr-2">🎉</span>
              <h2 className="text-xl font-bold text-green-800">
                Hangout confirmed!
              </h2>
            </div>
            {confirmedSlotStart && (
              <p className="text-green-700 text-lg">
                {formatConfirmedDate(confirmedSlotStart)}
              </p>
            )}
            {eventData.location && (
              <p className="text-green-600 mt-2">
                📍 {eventData.location.name}
              </p>
            )}
          </div>
        )}

        {/* Quorum Indicator */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">Response Progress</h2>
          <QuorumIndicator
            current={respondedCount}
            required={quorumRequired}
            total={totalParticipants}
            showWarning={respondedCount >= quorumRequired && !hasAnySlotMeetingQuorum}
          />
        </div>

        {/* Heatmap Visualization */}
        {heatmapData && heatmapData.slots && heatmapData.slots.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow p-4">
            <HeatmapVisualization eventId={eventData._id as Id<"events">} />
          </div>
        )}

        {/* Availability Picker — converted from prototype
            design-samples/pwa-availability-picker.html. */}
        {!submitted ? (
          // TODO(picker): organizerName/organizerPhotoUrl come from the event
          // organizer's user profile; the event/user join is not wired yet, so
          // we fall back to "Alex" + bundled photo to match the signed-off
          // design. peoplePileInitials is derived from participants where
          // possible; otherwise we use the prototype defaults.
          <AvailabilityPickerScreen
            eventTitle={eventData.title}
            organizerName="Alex"
            roughWindowLabel={eventData.timeframe ?? 'Soon'}
            locationLabel={eventData.location?.name}
            peopleCount={eventData.participants?.length ?? undefined}
            peoplePileInitials={
              eventData.participants && eventData.participants.length > 0
                ? eventData.participants
                    .slice(0, 3)
                    .map((p: { displayName?: string }) =>
                      p.displayName?.trim()?.[0]?.toUpperCase() ?? '?'
                    )
                : ['M', 'J', 'S']
            }
            days={demoDays}
            // TODO(picker): compute masked phone from
            // `participants[me].phone` once the "me" resolution lands on the
            // PWA token path. For now we hard-code the prototype mask.
            smsConsent={{
              maskedPhone: '(•••) •••‑1234',
              initialConsented: true,
            }}
            onSubmit={handleSubmit}
          />
        ) : (
          // TODO(invite-submitted): organizerName/organizerPhotoUrl are not available on
          // `eventData` today (only organizerId). Wire real organizer profile fields when
          // the event/user join lands. dateRangeLabel currently maps from `timeframe`.
          // submittedSlots is left empty until selectedSlots is upgraded to carry
          // structured day/time/availability data — do NOT invent a shape here.
          <InviteSubmittedScreen
            eventTitle={eventData.title}
            locationName={eventData.location?.name}
            dateRangeLabel={eventData.timeframe ?? 'Soon'}
            organizerName="Organizer"
            submittedSlots={[]}
          />
        )}
      </div>
    </div>
  );
}
